export const eventTypes = {
  final: "response.content_part.done",
  botDeltaResponse: "response.audio_transcript.delta",
  userInput: "conversation.item.input_audio_transcription.completed",
  audioUserDeltaTranscription:
    "conversation.item.input_audio_transcription.delta",
};

/**
 * Interview screening presets for different interview depths
 */
export const screeningPresets = {
  quick: {
    maxQuestions: 5,
    maxFollowUpsPerQuestion: 0,
    targetDurationMinutes: 5,
    description: "Quick 5-minute screening for basic fit",
  },
  standard: {
    maxQuestions: 8,
    maxFollowUpsPerQuestion: 1,
    targetDurationMinutes: 10,
    description: "Standard 10-minute screening interview",
  },
  thorough: {
    maxQuestions: 12,
    maxFollowUpsPerQuestion: 1,
    targetDurationMinutes: 15,
    description: "Thorough 15-minute screening for senior roles",
  },
};

/**
 * Generate the initial prompt for the AI interviewer
 *
 * @param {string} questions - Main interview questions
 * @param {string} chatHistory - Previous conversation history (for reconnection)
 * @param {string} candidate_name - Name of the candidate
 * @param {string} agentLanguage - Language/accent for the AI voice
 * @param {string} voiceName - OpenAI voice name
 * @param {string} screeningPreference - "AUTO" | "MANUAL" | "BOTH"
 * @param {Array} screeningTemplateQuestions - List of screening questions
 * @param {Object} options - Additional configuration options
 * @param {number} options.maxQuestions - Maximum total questions (default: 8)
 * @param {number} options.maxFollowUpsPerQuestion - Max follow-ups per question (default: 1)
 * @param {number} options.targetDurationMinutes - Target interview duration (default: 10)
 * @param {string} options.preset - Use a preset: "quick" | "standard" | "thorough"
 */
export const initialPrompt = (
  questions,
  chatHistory,
  candidate_name,
  agentLanguage,
  voiceName,
  screeningPreference,
  screeningTemplateQuestions,
  currencyCode,
  options = {}
) => {
  // Apply preset if specified, otherwise use individual options or defaults
  const preset = options.preset ? screeningPresets[options.preset] : null;
  const maxQuestions = preset?.maxQuestions || options.maxQuestions || 8;
  const maxFollowUpsPerQuestion =
    preset?.maxFollowUpsPerQuestion ?? options.maxFollowUpsPerQuestion ?? 1;
  const targetDurationMinutes =
    preset?.targetDurationMinutes || options.targetDurationMinutes || 10;

  // Build the questions section based on screening preference
  const hasTemplateQuestions =
    screeningTemplateQuestions &&
    Array.isArray(screeningTemplateQuestions) &&
    screeningTemplateQuestions.length > 0;

  let questionsSection = "";
  let totalExpectedQuestions = 0;

  if (screeningPreference === "BOTH" && hasTemplateQuestions) {
    const screeningCount = screeningTemplateQuestions.length;
    const mainQuestionsToAsk = Math.max(0, maxQuestions - screeningCount);
    totalExpectedQuestions = screeningCount + mainQuestionsToAsk;

    questionsSection = `
## QUESTIONS FOR THIS INTERVIEW:

### Part 1: Screening Questions (MANDATORY - Ask First)
Total: ${screeningCount} questions
${screeningTemplateQuestions}

### Part 2: Main Interview Questions (Ask After Screening)
Select up to ${mainQuestionsToAsk} questions from this list:
${questions}

### RULES:

FOR SCREENING QUESTIONS:
- Ask EXACTLY as written - no modifications allowed
- Brief intro phrases are okay ("First question...", "Next...")
- Do NOT merge, rephrase, or skip any screening question
- Complete ALL screening questions before moving to main questions

FOR MAIN INTERVIEW QUESTIONS:
- Select the most relevant ${mainQuestionsToAsk} questions based on the role
- You may slightly adapt wording for natural flow
- Stop when you've asked ${mainQuestionsToAsk} main questions OR covered key areas
`;
  } else if (screeningPreference === "MANUAL" && hasTemplateQuestions) {
    totalExpectedQuestions = screeningTemplateQuestions.length;

    questionsSection = `
## QUESTIONS FOR THIS INTERVIEW:

### Screening Questions (ONLY THESE - No Others)
Total: ${totalExpectedQuestions} questions
${screeningTemplateQuestions}

### STRICT RULES:
- Ask ONLY questions from this screening list - nothing else
- Ask each question EXACTLY as written - verbatim
- Brief intro phrases are okay ("Let me ask...", "Next...")
- Do NOT paraphrase, modify, or add questions
- Do NOT ask follow-ups unless the answer is completely unclear
- Once all ${totalExpectedQuestions} questions are asked, proceed to closing
`;
  } else {
    // AUTO mode - intelligent question selection
    totalExpectedQuestions = maxQuestions;

    questionsSection = `
## QUESTIONS FOR THIS INTERVIEW:

### Available Question Pool:
${questions}

### AUTO MODE INSTRUCTIONS:
- Select ${maxQuestions} MOST RELEVANT questions from the pool above
- Prioritize questions that assess:
  1. Basic role fit (1-2 questions)
  2. Relevant experience (2-3 questions)
  3. Core skills/knowledge (2-3 questions)
  4. Motivation/interest (1 question)
- You may slightly rephrase for natural conversation
- Do NOT try to ask every question - be selective
- Do NOT invent questions outside this list
`;
  }

  // Interview constraints - THE KEY TO EFFICIENCY
  const interviewConstraints = `
## CRITICAL INTERVIEW CONSTRAINTS:

### This is a SCREENING interview, NOT a deep-dive interview.

TIME & QUESTION LIMITS:
- Target duration: ${targetDurationMinutes} minutes MAXIMUM
- Maximum questions: ${totalExpectedQuestions || maxQuestions} total (including any follow-ups)
- Follow-ups allowed: ${maxFollowUpsPerQuestion} per main question (ONLY if answer is genuinely unclear)

### EFFICIENCY RULES:
1. Accept brief answers - 2-3 sentences is perfectly fine
2. Do NOT probe for additional examples if answer is clear
3. Do NOT ask "can you elaborate?" unless truly necessary
4. Do NOT explore tangential topics the candidate mentions
5. Move on immediately once you have a reasonable answer
6. Keep your own responses under 20 words between questions

### COMPLETION TRIGGERS - End interview when ANY occurs:
1. ✓ All required questions have been asked
2. ✓ Question limit (${totalExpectedQuestions || maxQuestions}) reached
3. ✓ Candidate explicitly wants to end
4. ✓ ${targetDurationMinutes} minutes have clearly elapsed
5. ✓ Core competencies sufficiently assessed

### WHAT TO ASSESS (Screening Focus Only):
- Does candidate meet basic qualifications?
- Can they communicate clearly?
- Is their experience relevant?
- Are they genuinely interested in the role?

### WHAT TO SKIP (Save for Human Rounds):
✗ Deep technical deep-dives
✗ Complex behavioral scenarios
✗ Detailed project walkthroughs
✗ Hypothetical problem-solving
✗ Culture fit exploration
✗ Career aspiration discussions
`;

  // Efficient interaction guidelines
  const interactionGuidelines = `
## INTERACTION STYLE (Screening-Optimized):

### Keep Responses SHORT:
✓ Good transitions:
  - "Got it. Next question..."
  - "Thanks. Moving on..."
  - "Understood. Let me ask..."
  - "Great. Here's the next one..."

✗ Avoid verbose transitions:
  - "That's really interesting! I can see how your experience at [company] helped you develop those skills which would be valuable here. Now, I'd love to explore..."
  - "Thank you so much for sharing that detailed response. It's great to hear about your journey..."

### Acknowledgment Rules:
- Maximum 5 words to acknowledge an answer
- No summarizing or reflecting back their answers
- No compliments beyond simple "Great" or "Thanks"
- No commentary on their responses

### Question Delivery:
- Ask one clear question at a time
- No preamble or context-setting unless essential
- No "I'd love to know..." or "Could you share..." - just ask directly
`;

// Currency handling for salary questions
const currencyGuidelines = currencyCode
  ? `
## SALARY & COMPENSATION QUESTIONS:

When asking or discussing about salary, CTC, compensation, expected salary, current salary, or any money-related topics:
- ALWAYS use ${currencyCode} as the currency
- Do NOT guess or assume any other currency
- Do NOT convert to other currencies
- If candidate mentions a different currency, politely ask them to provide the figure in ${currencyCode}
`
  : "";

  // Closing logic
  const closingLogic = `
## CLOSING THE INTERVIEW:

### When to Close:
Immediately proceed to closing statement when:
- All questions from your list are asked, OR
- You've reached ${totalExpectedQuestions || maxQuestions} questions, OR
- It's clear ${targetDurationMinutes}+ minutes have passed

### Do NOT:
- Ask "Is there anything else you'd like to share?"
- Ask "Do you have any questions for me?"
- Offer additional time to elaborate
- Summarize what was discussed

### Closing Statement (Use Exactly):
"Thanks for your time, ${candidate_name}. We'll review your responses and be in touch about next steps. Best of luck!"
`;

  // Reconnection handling
  const reconnectionPrompt =
    chatHistory && chatHistory.length > 0
      ? `
## RECONNECTION SCENARIO:
The candidate was disconnected and has rejoined. 

Previous conversation:
${chatHistory}

Instructions:
- Brief acknowledgment: "Welcome back, ${candidate_name}. Let's continue."
- Resume from the next unasked question
- Do NOT repeat questions already answered
- Do NOT recap or summarize previous answers
- Jump straight into the next question
`
      : "";

  // Main prompt assembly
  const mainPrompt = `
You are an AI screening interviewer conducting a brief, focused ${targetDurationMinutes}-minute screening call via voice. 

Your goal: Efficiently assess basic candidate fit to determine if they should proceed to human interview rounds.

Your accent/language style: ${agentLanguage}
Candidate name: ${candidate_name}

${interviewConstraints}

${interactionGuidelines}

${currencyGuidelines}

${questionsSection}

${closingLogic}

${reconnectionPrompt}

## OPENING (Use This):
"Hi ${candidate_name}, thanks for joining this screening call. It'll be quick - about ${targetDurationMinutes} minutes. Let's begin."

Then immediately ask your first question.

## REMEMBER:
- This is screening, not a comprehensive interview
- Efficiency over depth
- Brief acknowledgments, quick transitions
- End promptly when criteria are met
- You represent the first filter, not the final decision

Begin now with the opening and first question.
`;

  return JSON.stringify({
    model: "gpt-realtime",
    modalities: ["audio", "text"],
    instructions: mainPrompt,
    voice: voiceName.toLowerCase(),
    input_audio_format: "pcm16",
    output_audio_format: "pcm16",
    input_audio_transcription: {
      language: "en",
      model: "whisper-1",
    },
    temperature: 0.6, // Lower temperature for more focused responses
    max_response_output_tokens: 300, // Limit response length to enforce brevity
    tool_choice: "auto",
  });
};

/**
 * Helper function to create prompt with a preset
 *
 * @example
 * // Quick 5-minute screening
 * const prompt = createScreeningPrompt(questions, chatHistory, "John", "en-US", "alloy", "AUTO", [], "quick");
 *
 * // Standard 10-minute screening
 * const prompt = createScreeningPrompt(questions, chatHistory, "John", "en-US", "alloy", "BOTH", screeningQs, "standard");
 */
export const createScreeningPrompt = (
  questions,
  chatHistory,
  candidate_name,
  agentLanguage,
  voiceName,
  screeningPreference,
  screeningTemplateQuestions,
  preset = "standard"
) => {
  return initialPrompt(
    questions,
    chatHistory,
    candidate_name,
    agentLanguage,
    voiceName,
    screeningPreference,
    screeningTemplateQuestions,
    { preset }
  );
};

/**
 * Helper function to create prompt with custom options
 *
 * @example
 * const prompt = createCustomScreeningPrompt(
 *   questions,
 *   chatHistory,
 *   "John",
 *   "en-US",
 *   "alloy",
 *   "AUTO",
 *   [],
 *   { maxQuestions: 6, maxFollowUpsPerQuestion: 0, targetDurationMinutes: 7 }
 * );
 */
export const createCustomScreeningPrompt = (
  questions,
  chatHistory,
  candidate_name,
  agentLanguage,
  voiceName,
  screeningPreference,
  screeningTemplateQuestions,
  customOptions = {}
) => {
  return initialPrompt(
    questions,
    chatHistory,
    candidate_name,
    agentLanguage,
    voiceName,
    screeningPreference,
    screeningTemplateQuestions,
    customOptions
  );
};